import { useEffect } from 'react'

import { atom, useAtomValue, useSetAtom, type Getter } from 'jotai'
import invariant from 'tiny-invariant'

import { resolveNodeDefinitions } from './resolveNodeDefinitions'
import { ResolvedNodeDefinitions } from './types'

/**
 * The fully-resolved node-definition registry for the current surface (editor
 * or watchtower). Each jotai Provider holds its own value, populated once at
 * load by `usePopulateResolvedNodeDefinitions`. `undefined` until resolution
 * completes — surfaces gate on this before reading definitions.
 *
 * Prefer `useResolvedNodeDefinitions` / `getResolvedNodeDefinitions` over
 * reading this directly: callers within a loaded surface can treat it as
 * always present. The populate hook and gates read the raw atom.
 */
export const resolvedNodeDefinitionsAtom = atom<
  ResolvedNodeDefinitions | undefined
>(undefined)

const NOT_READY_MESSAGE =
  'Resolved node definitions read before the surface finished loading — access them only once populated (gated by usePopulateResolvedNodeDefinitions).'

/**
 * Resolves the registry once on mount into the atom. Returns whether the
 * registry is ready, so the surface can gate its interactive UI. Used by both
 * the editor (`useLoadAutomationEditor`) and the watchtower inbox.
 */
export const usePopulateResolvedNodeDefinitions = (): boolean => {
  const definitions = useAtomValue(resolvedNodeDefinitionsAtom)
  const setDefinitions = useSetAtom(resolvedNodeDefinitionsAtom)

  useEffect(() => {
    let cancelled = false
    resolveNodeDefinitions().then(resolved => {
      if (!cancelled) setDefinitions(resolved)
    })
    return () => {
      cancelled = true
    }
  }, [setDefinitions])

  return Boolean(definitions)
}

/**
 * Reads the resolved registry for React consumers within a loaded surface. The
 * surface gates its interactive UI on the registry being populated, so the
 * invariant always holds here — call sites get a non-nullable value and skip
 * null checks.
 */
export const useResolvedNodeDefinitions = (): ResolvedNodeDefinitions => {
  const definitions = useAtomValue(resolvedNodeDefinitionsAtom)
  invariant(definitions, NOT_READY_MESSAGE)
  return definitions
}

/**
 * The same invariant for jotai atom read/write contexts, which can't call the
 * hook. Only reached from interactions within a loaded surface.
 */
export const getResolvedNodeDefinitions = (
  get: Getter
): ResolvedNodeDefinitions => {
  const definitions = get(resolvedNodeDefinitionsAtom)
  invariant(definitions, NOT_READY_MESSAGE)
  return definitions
}
